<template>
  <div class="w-full" :key="id">
    <NuxtLink
      :to="url"
      class="block w-full"
      :aria-label="hasAnyPrice ? `${name}, price ${wholesalePriceStr || retailPriceStr}` : name"
      :title="name"
      :id="`product-card-${indexValue}`"
      external
      @click="onProductClick"
    >
      <div
        role="group"
        :tabindex="isMobile ? 0 : undefined"
        v-click-outside="deactivate"
        @mouseenter="activate"
        @mouseleave="deactivate"
        @focus="activate"
        @click="activate"
        class="relative flex items-center justify-center rounded-[0.571rem]
               bg-ash-400 after:block after:pb-[100%]
               group overflow-hidden"
      >
        <NuxtImg
          :src="primaryImageUrl"
          :alt="name"
          :title="name"
          loading="lazy"
          :class="[ 
            'max-h-[17.14rem]', 
            'transition-transform duration-200 ease-out', 
            'transform-gpu will-change-transform', 
            'group-hover:blur-[0.928rem]',  
            isActive && 'blur-[0.928rem]'  
          ]"
        />
        <span v-show="normalizedTag" 
          class="absolute top-3 left-3 text-sm text-ash-500 
          group-hover:hidden group-focus:hidden" 
          :style="normalizedTag?.color ? { color: normalizedTag.color } : undefined"
          >
          {{ normalizedTag?.text }}
        </span>
        <div
          v-if="hasAnyPrice"
          class="absolute bottom-0 left-0 ml-[0.714rem] mb-[0.714rem] rounded bg-white/90 text-sm font-medium text-gray-900" >
          <span v-if="wholesalePriceStr" class="px-3 text-[0.857rem] font-normal text-ash-500 bg-white rounded-[0.2142857143rem] flex justify-center items-center h-[1.7143rem] mb-[0.5]">
            {{ wholesalePriceStr }}
          </span>
          <span v-if="retailPriceStr" class="px-3 py-1 text-[0.857rem] font-normal text-ash-500 bg-white rounded-[0.2142857143rem] items-center h-[1.7143rem]">
            {{ retailPriceStr }}
          </span>
        </div>
        <div
          :class="[
            'absolute inset-0 flex flex-col items-center justify-center text-left',
            'bg-black/10',
            'transition-[transform,opacity] duration-300 ease-out',
            'will-change-transform will-change-opacity transform-gpu',
            '-translate-y-full opacity-0',
            'group-hover:translate-y-0 group-hover:opacity-100',
            isActive && 'translate-y-0 opacity-100'
          ]"
        >
          <FavoriteButton
            v-if="id"
            class="absolute left-6 top-4"
            :logged-in="userStore.loggedIn"
            item-type="product"
            :is-favorite="isFavorite(id)"
            @toggle="toggleFavorite({ id })"
            :id="`prod-list-fav-${indexValue}`"
            @click.stop
          />
          <div class="px-4 text-gray-700">
            <p class="text-[0.857rem] mb-[1rem] line-clamp-2">
              {{ headline }}
            </p>
            <p class="text-[0.857rem] mb-[1rem] line-clamp-5">
              {{ shortDescription }}
            </p>
          </div>
        </div>
      </div>

    <div class="pt-3" ref="productTitle">
        <Heading :level="5" class="text-gray-800">
          {{ name }}
        </Heading>
        <p class="!text-[0.875rem] font-normal text-gray-700">
          {{ benefits }}
        </p>
    </div>
  </NuxtLink>
  <div @click.stop class="relative">
      <AddToCart ref="addToCartRef" :productId="id" :id="indexValue"  :style="{ bottom: adjustedHeight + 'rem' }"  :productName="name" :analyticsContext="analyticsContext" class="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"/>
  </div>
   <StarRating
        class="text-[0.7142857143rem]"
        :rating="reviewSummary.averageRating"
      />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from 'vue'
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'
import { useUserStore } from '~/stores/userStore'
import { useLocaleStore } from '~/stores/localeStore'
import { useGtmStore } from '~/stores/googleTagManagerStore'
import StarRating from '../../atom/starRating.vue'
import Heading from '../../atom/heading.vue'
import AddToCart from '../../organism/product/addToCart.vue'
import FavoriteButton from '../../atom/FavoriteButton.vue'
import type { ProductCardProps, LegacyPricing } from '~~/shared/types/common'

const breakPoints = useBreakpoints(breakpointsTailwind);
const isMobileBreakpoint = breakPoints.smaller('md'); 
const isMobile = ref(isMobileBreakpoint.value);

const userStore = useUserStore()
const emit = defineEmits(['toggle-favorite'])

const {
  id = 0,
  sku,
  name = '',
  url = '',
  price = '',
  primaryImageUrl = '',
  benefits = '',
  shortDescription = '',
  headline = '',
  reviewSummary = { reviewCount: 0, averageRating: 0 },
  info = '',
  tag,
  indexValue,
  favoriteIds = [],
  pricing = {} as LegacyPricing,
  analyticsContext,
} = defineProps<ProductCardProps & {
  price?: string
  pricing?: LegacyPricing
}>()

const pdpSkuCookie = useCookie('pdpsku', {
  path: '/',
  sameSite: 'lax'
})

const onProductClick = () => {
  if (sku) {
    pdpSkuCookie.value = sku
  }
  useGtmStore().selectProductEvent(analyticsContext ?? null)
  isActive.value = true;
}

const isFavorite = (id?: number | string) => {
  if (!id) return false
  const normalizedId = typeof id === 'string' ? Number(id) : id
  return favoriteIds.includes(normalizedId)
}

const toggleFavorite = async (item: { id?: number | string }) => {
  if (!item?.id) return
  const normalizedId = typeof item.id === 'string' ? Number(item.id) : item.id
  emit('toggle-favorite', normalizedId)
}

const isProfessional = computed(() => {
  const localeStore = useLocaleStore()
  return localeStore.isProfessional
})

const isActive = ref(false)

const activate = () => (isActive.value = true)
const deactivate = () => (isActive.value = false)

const formatPrice = (val?: string | null): string => val ? val.replaceAll('.00', '') : ''

const wholesalePriceStr = computed(() => {
  const wp = pricing?.wholesalePrice
  if (isProfessional.value && wp !== null && wp !== undefined) {
    const fp = formatPrice(wp)
    return fp ? `${fp} Wholesale` : ''
  }
  return ''
})

const retailPriceStr = computed(() => {
  let base: string | undefined

  if (
    pricing?.hasMultiplePrices &&
    pricing?.priceRange &&
    pricing.priceRange.lowPrice !== null && pricing.priceRange.lowPrice !== undefined &&
    pricing.priceRange.highPrice !== null && pricing.priceRange.highPrice !== undefined
  ) {
    base = `${pricing.priceRange.lowPrice}-${pricing.priceRange.highPrice}`
  } else if (pricing?.retailPrice !== null && pricing?.retailPrice !== undefined) {
    base = pricing.retailPrice as string
  } else if (pricing?.price !== null && pricing?.price !== undefined) {
    base = pricing.price as string
  } else if (price) {
    base = price
  }

  if (!base) return ''

  if (wholesalePriceStr.value) {
    base = `${base} MSRP`
  }

  return formatPrice(base)
})

const hasAnyPrice = computed(() => !!(wholesalePriceStr.value || retailPriceStr.value))

const addToCartRef = ref()
const productTitle = ref<HTMLDivElement | null>(null)
const adjustedHeight = ref(0)

onMounted(async () => {
  await nextTick()
  requestAnimationFrame(() => { 
    if (productTitle.value) {
      const height = productTitle.value.offsetHeight
      adjustedHeight.value = ((height + 10.5) / 14 );
    }
  })
  isActive.value = false;
  window.addEventListener('pageshow', () => { isActive.value = false })
})

const normalizedTag = computed(() => {
  if (!tag) return null
  if (
    typeof tag === 'object' &&
    tag !== null &&
    'value' in tag
  ) {
    return {
      text: tag.value as string,
      color: 'hexColor' in tag ? (tag.hexColor as string | undefined) : undefined
    }
  }
  return {
    text: tag as string,
    color: undefined
  }
})

</script>
